import { isNativeOAuthReturn } from './nativeOAuthReturn';
import { isNativeCapacitorPlatform, openExternal } from './bridge';
import { type AppEnv, otherEnv, runningEnv } from '../env/appEnv';
import { captureReferralFromSearch } from '../auth/referralCapture';

export const NATIVE_DEEP_LINK_EVENT = 'lorath:native-deep-link';

export interface NativeDeepLinkDetail {
  path: string;
  url: string;
}

// R6-02-c: Verweise unterscheiden die Umgebungen. Frueher galten BEIDE Hosts
// als „intern": ein Produktions-Link (Mail, Push, Anzeige) oeffnete im
// Beta-Modus einen Beta-Bildschirm mit einer Prod-Kennung — falscher Inhalt,
// 404 oder im schlechten Fall ein fremder Datensatz mit derselben Nummer.
// Intern ist jetzt nur noch der Host der LAUFENDEN Umgebung; der andere geht
// nach draussen.
const DEEP_LINK_HOST_BY_ENV: Record<AppEnv, string> = {
  production: 'app.lorath.eu',
  beta: 'beta.lorath.eu',
};

export function deepLinkHostFor(env: AppEnv): string {
  return DEEP_LINK_HOST_BY_ENV[env];
}

export type DeepLinkHostKind = 'own' | 'other' | 'unknown';

export function classifyDeepLinkHost(host: string, env: AppEnv = runningEnv()): DeepLinkHostKind {
  if (host === deepLinkHostFor(env)) return 'own';
  if (host === deepLinkHostFor(otherEnv(env))) return 'other';
  return 'unknown';
}
let deepLinkInstallPromise: Promise<void> | null = null;

function getWindow(): Window | null {
  return typeof window === 'undefined' ? null : window;
}

function rawPathFromUrl(rawUrl: string): string {
  const match = rawUrl.match(/^[a-z][a-z\d+.-]*:\/\/[^/?#]*(?<path>[^?#]*)/i);
  const path = match?.groups?.path;
  return path === undefined || path === '' ? '/' : path;
}

/**
 * Prueft, ob ein Pfad als interner SPA-Pfad sicher ist (keine Protokoll-Relatives,
 * kein Path-Traversal, kein Backslash). Wird sowohl fuer Universal-Links
 * (Host-Whitelist in `toAppPath`) als auch fuer serverseitig eingebettete
 * Push-Routen (`additionalData.route`) wiederverwendet — gleiche Regeln,
 * gleicher Schutz.
 */
export function isSafeAppPath(pathname: string): boolean {
  if (!pathname.startsWith('/') || pathname.startsWith('//') || pathname.startsWith('/\\')) return false;
  let decoded: string;
  try {
    decoded = decodeURIComponent(pathname);
  } catch {
    return false;
  }
  if (!decoded.startsWith('/') || decoded.startsWith('//') || decoded.includes('\\')) return false;
  return !decoded.split('/').some((segment) => segment === '..');
}

function toAppPath(rawUrl: string): string | null {
  try {
    const url = new URL(rawUrl);
    if (url.protocol !== 'https:') return null;
    if (classifyDeepLinkHost(url.host) !== 'own') return null;
    const pathname = url.pathname || '/';
    if (!isSafeAppPath(rawPathFromUrl(rawUrl)) || !isSafeAppPath(pathname)) return null;
    return `${pathname}${url.search}`;
  } catch {
    return null;
  }
}

/**
 * Server- und Abmeldepfade duerfen nie im SPA-Router landen. Android faengt
 * derzeit den kompletten App-Host ab; das externe Oeffnen sorgt dafuer, dass
 * der Request trotzdem den Server beziehungsweise dessen Browser-Redirect
 * erreicht. Die Segmentpruefung verhindert Fehlklassifizierungen wie /apix.
 */
export function shouldOpenDeepLinkExternally(pathname: string): boolean {
  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(pathname).toLowerCase();
  } catch {
    return false;
  }

  // Spiegel der AASA-Ausnahmen (server/well-known.js) fuer Android, das den
  // ganzen Host abfaengt: Dateien, OTA und Print-Kurzlinks gehoeren in den Browser.
  return ['/api', '/unsubscribe', '/abmelden', '/uploads', '/ota', '/qr', '/abizeitung', '/.well-known'].some(
    (prefix) => decodedPath === prefix || decodedPath.startsWith(`${prefix}/`),
  );
}

export async function dispatchDeepLink(rawUrl: string): Promise<void> {
  const win = getWindow();
  if (!win) return;

  // Reservierte OAuth-Rückgaben niemals an Router, Referral-Erfassung oder
  // externen Browser weitergeben, auch ohne passende lokale Transaktion.
  if (isNativeOAuthReturn(rawUrl)) {
    const { consumeNativeOAuthReturn } = await import('./nativeOAuth');
    if (await consumeNativeOAuthReturn(rawUrl)) return;
  }

  // Verweis in die jeweils ANDERE Umgebung: nicht in den SPA-Router, sondern
  // nach draussen. Dort gilt eine andere Datenbank, dieselbe Kennung meint dort
  // etwas anderes.
  try {
    const parsed = new URL(rawUrl);
    if (parsed.protocol === 'https:' && classifyDeepLinkHost(parsed.host) === 'other') {
      try {
        await openExternal(rawUrl);
      } catch {
        // Kein Logging: die URL kann Token tragen.
      }
      return;
    }
  } catch {
    return;
  }

  const path = toAppPath(rawUrl);
  if (!path) return;

  try {
    const parsed = new URL(rawUrl);
    if (shouldOpenDeepLinkExternally(parsed.pathname)) {
      // Die Original-URL bleibt inklusive Query-Token unveraendert. Fehler des
      // Browser-Plugins werden bewusst nicht in eine SPA-Navigation umgelenkt.
      try {
        await openExternal(rawUrl);
      } catch {
        // Kein Logging: Die URL kann einen geheimen Abmelde-Token enthalten.
      }
      return;
    }
  } catch {
    return;
  }

  // Audit 05-P1-13: Referral-Code aus nativen Deep-Links (Cold-Start via
  // getLaunchUrl + Warm-Open via appUrlOpen) erfassen, BEVOR der Listener
  // navigiert. Capture ist no-op, wenn rawUrl kein gueltigen Parser ergibt
  // oder kein ?ref= enthaelt; toAppPath hat die URL bereits validiert.
  try {
    const parsed = new URL(rawUrl);
    captureReferralFromSearch(parsed.search);
  } catch {
    // Doppelte Robustheit: sollte nie greifen, weil toAppPath oben bereits
    // erfolgreich geparst hat. Stillschweigend schlucken, damit der
    // Deep-Link-Flow bei unerwarteten URL-Formen nicht haengt.
  }

  win.dispatchEvent(new CustomEvent<NativeDeepLinkDetail>(NATIVE_DEEP_LINK_EVENT, {
    detail: { path, url: rawUrl },
  }));
}

async function getAppModule(): Promise<typeof import('@capacitor/app') | null> {
  if (!await isNativeCapacitorPlatform()) return null;
  try {
    const appModule = await import('@capacitor/app');
    const { Capacitor } = await import('@capacitor/core');
    return Capacitor.isPluginAvailable('App') ? appModule : null;
  } catch {
    return null;
  }
}

// 7.4.1: Kaltstart per Link liefert dieselbe URL ZWEIMAL — einmal ueber
// getLaunchUrl (iOS: ApplicationDelegateProxy.lastURL, Android: Intent-URI)
// und einmal als zurueckgehaltenes appUrlOpen (retainUntilConsumed), das
// Capacitor beim addListener nachreicht. Ohne Entdopplung legt der Router den
// Pfad zweimal in die History, Zurueck braeuchte dann zwei Schritte. Das
// Zeitfenster haelt einen echten zweiten Aufruf desselben Links spaeter
// nicht auf; getLaunchUrl bleibt der Rueckfallpfad, falls das Plugin beim
// Link noch nicht geladen war und kein appUrlOpen nachkommt.
const LAUNCH_URL_ECHO_WINDOW_MS = 5_000;

export function installDeepLinkListener(): void {
  if (deepLinkInstallPromise) return;

  deepLinkInstallPromise = (async () => {
    const appModule = await getAppModule();
    if (!appModule) return;

    const launchUrl = (await appModule.App.getLaunchUrl())?.url ?? null;
    if (launchUrl) await dispatchDeepLink(launchUrl);

    let launchEcho = launchUrl
      ? { url: launchUrl, until: Date.now() + LAUNCH_URL_ECHO_WINDOW_MS }
      : null;
    await appModule.App.addListener('appUrlOpen', (event) => {
      // Nur das ERSTE Ereignis kann das Echo des Kaltstart-Links sein.
      const echo = launchEcho;
      launchEcho = null;
      if (echo && event.url === echo.url && Date.now() <= echo.until) return;
      void dispatchDeepLink(event.url);
    });
  })();
}

export function subscribeDeepLinkNavigation(callback: (path: string) => void): () => void {
  const win = getWindow();
  if (!win) return () => undefined;

  const handler = (event: Event) => {
    const detail = (event as CustomEvent<NativeDeepLinkDetail>).detail;
    if (detail?.path) callback(detail.path);
  };

  win.addEventListener(NATIVE_DEEP_LINK_EVENT, handler);
  return () => win.removeEventListener(NATIVE_DEEP_LINK_EVENT, handler);
}
